<template>
  <div ref="map" class="retailers-map-section">
    <p v-if="errorMessage" class="error-label text-center">{{errorMessage}}</p>
      <svg class="line-graph-svg"
           :height="tallerHeight"
           :width="width">
        <g v-if="mapData && mapPath"
           class="region-paths"
           :transform="`translate(0, ${margin / 2})`">
          <path v-for="state in mapData" :key="state.id" :d="mapPath(state)"></path>
        </g>
        <g v-if="geoPoints"
           class="coordinates-paths"
           :transform="`translate(0, ${margin / 2})`">
          <circle v-for="(point, index) in geoPoints"
                  :key="index"
                  :cx="point.cx"
                  :cy="point.cy" r="4"></circle>
        </g>
      </svg>
  </div>
</template>

<script>
  import * as d3 from 'd3'
  //https://s3-us-west-2.amazonaws.com/s.cdpn.io/39255/us-states.json -- where geoData came from
  import usGeoData from './usGeoJSONdata.json'

  export default {
    props: {
      currentAdvertiserId: [Number, String],
    },

    name: 'RetailersMap',

    data() {
      return {
        errorMessage: null,
        geoPoints: null,
        height: null,
        margin: 20,
        mapData: null,
        mapPath: null,
        projection: null,
        retailerCoordinates: null,
        retailerRegions: null,
        usStates: null,
        width: null,
      }
    },

    methods: {
      buildGeoPoints() {
        let geoPoints = null
        if (this.retailerCoordinates && this.retailerCoordinates.length >= 1 && this.projection) {
          geoPoints = []
          this.retailerCoordinates.forEach((coords) => {
            const coordPositions = this.projection([coords[0], coords[1]])
            if (coordPositions) {
              geoPoints.push({
                cx: coordPositions[0],
                cy: coordPositions[1]
              })
            }
          })
        }
        this.geoPoints = geoPoints
      },

      findSingleRegion(region) {
        const states = this.usStates
        return states.find( ({ properties }) => properties.name.toLowerCase() === region.toLowerCase() )
      },

      loadRetailersGeoData() {
        let errorText = 'there was an error loading the geo data'
        this.$api.get(`/merchants/geo_data?brand_id=${this.currentAdvertiserId}`)
          .then((response) => {
            const data = response.data
            if (data && typeof data !== 'string') {
              this.retailerCoordinates = data.merchant_coordinates
              this.retailerRegions = data.merchant_regions
              this.errorMessage = null
              this.$emit('setRegionsForFilter', data.merchant_regions)              
            } else {
              this.retailerCoordinates = null
              this.retailerRegions = null
              this.errorMessage = `${errorText}, please refresh or contact support`
            }
            this.setMapDimensions()
          })
          .catch((error) => {
            this.retailerCoordinates = null
            this.retailerRegions = null
            this.errorMessage = `${errorText}: ${error}`
            this.setMapDimensions()
          })
      },

      scaleMap() {
        let scaleData
        if (this.retailerRegions && this.retailerRegions.length === 1) {
          scaleData = this.findSingleRegion(this.retailerRegions[0])
          this.mapData = scaleData
          this.projection = d3.geoMercator()
        } else {
          scaleData = usGeoData
          this.mapData = this.usStates
          this.projection = d3.geoAlbersUsa()
        }
        this.projection.fitSize([this.width, this.height], scaleData)
        this.mapPath = d3.geoPath().projection(this.projection)
        this.buildGeoPoints()
      },

      setMapDimensions() {
        const $map = this.$refs.map
        let width = $map ? $map.offsetWidth : 250,
          height = width * 0.7
        height = height > 580 ? 580 : height
        this.width = width
        this.height = height
        this.scaleMap()
      },
    },

    created() {
      this.usStates = usGeoData.features
      this.loadRetailersGeoData()
    },

    mounted() {
      window.addEventListener('resize', this.setMapDimensions)
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.setMapDimensions)
    },

    computed: {
      tallerHeight: function () {
        return this.height + this.margin
      }
    },
  }
</script>

<style lang="sass">
  @import '../../assets/sass/colors'
  //
  //  retailers map
  //
  .retailers-map-section
    flex: 1
    max-width: 40%
    @media only screen and (max-width: 40em)
      max-width: 100%
      width: 100%

    circle
      fill: $primary-color

    .region-paths path
      fill: none
      stroke: black

    .error-label
      color: $negative-color
</style>